import { assert } from '@sindresorhus/is';
import { useContext } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { UrlNavigationContext } from './UrlNavigationContext';
import type { UrlNavigationStore, UrlNavigationStoreActions } from './url-navigation-store';

export const userUrlNavigationStore = (): UrlNavigationStore => {
    const urlNavigationContext = useContext(UrlNavigationContext);
    assert.plainObject(urlNavigationContext);

    return useStore(
        urlNavigationContext,
        useShallow((state) => ({
            activeJobArchitectureId: state.activeJobArchitectureId,
            clearComparing: state.clearComparing,
            clearSelection: state.clearSelection,
            closeArchiveDetails: state.closeArchiveDetails,
            closeChangelogDetails: state.closeChangelogDetails,
            closeClusterDetails: state.closeClusterDetails,
            closeDetails: state.closeDetails,
            closeFamilyDetails: state.closeFamilyDetails,
            closeSidepanel: state.closeSidepanel,
            closeSkillsDetails: state.closeSkillsDetails,
            hasSidepanelTypeOpen: state.hasSidepanelTypeOpen,
            initializeSelectionFromUrl: state.initializeSelectionFromUrl,
            isLastActiveSegment: state.isLastActiveSegment,
            navigateToArchitecture: state.navigateToArchitecture,
            openArchiveDetails: state.openArchiveDetails,
            openChangelogDetails: state.openChangelogDetails,
            openClusterDetails: state.openClusterDetails,
            openFamilyDetails: state.openFamilyDetails,
            openJobProfileDetails: state.openJobProfileDetails,
            openSidepanel: state.openSidepanel,
            openSkillsDetails: state.openSkillsDetails,
            selectedClusterId: state.selectedClusterId,
            selectedFamilyId: state.selectedFamilyId,
            selectedLevelId: state.selectedLevelId,
            selectedRoleId: state.selectedRoleId,
            setComparing: state.setComparing,
            setSelectedClusterId: state.setSelectedClusterId,
            setSelectedFamilyId: state.setSelectedFamilyId,
            setSelectedLevelId: state.setSelectedLevelId,
            setSelectedRoleId: state.setSelectedRoleId,
            sidepanels: state.sidepanels,
            updateJobProfileLanguage: state.updateJobProfileLanguage,
            updateSkillsPanelIfOpen: state.updateSkillsPanelIfOpen,
            updateSkillsProficiencyIfOpen: state.updateSkillsProficiencyIfOpen,
        })),
    );
};

export const useActiveJobArchitectureId = (): string => {
    const urlNavigationContext = useContext(UrlNavigationContext);
    assert.plainObject(urlNavigationContext);

    return useStore(urlNavigationContext, (state) => state.activeJobArchitectureId);
};

export const useUrlNavigationActions = (): Pick<
    UrlNavigationStoreActions,
    | 'clearComparing'
    | 'closeArchiveDetails'
    | 'closeChangelogDetails'
    | 'closeClusterDetails'
    | 'closeDetails'
    | 'closeFamilyDetails'
    | 'closeSidepanel'
    | 'closeSkillsDetails'
    | 'hasSidepanelTypeOpen'
    | 'initializeSelectionFromUrl'
    | 'navigateToArchitecture'
    | 'openArchiveDetails'
    | 'openChangelogDetails'
    | 'openClusterDetails'
    | 'openFamilyDetails'
    | 'openJobProfileDetails'
    | 'openSidepanel'
    | 'openSkillsDetails'
    | 'setComparing'
    | 'setSelectedLevelId'
    | 'setSelectedRoleId'
    | 'updateJobProfileLanguage'
    | 'updateSkillsPanelIfOpen'
    | 'updateSkillsProficiencyIfOpen'
> => {
    const urlNavigationContext = useContext(UrlNavigationContext);
    assert.plainObject(urlNavigationContext);

    return useStore(
        urlNavigationContext,
        useShallow((state) => ({
            clearComparing: state.clearComparing,
            closeArchiveDetails: state.closeArchiveDetails,
            closeChangelogDetails: state.closeChangelogDetails,
            closeClusterDetails: state.closeClusterDetails,
            closeDetails: state.closeDetails,
            closeFamilyDetails: state.closeFamilyDetails,
            closeSidepanel: state.closeSidepanel,
            closeSkillsDetails: state.closeSkillsDetails,
            hasSidepanelTypeOpen: state.hasSidepanelTypeOpen,
            initializeSelectionFromUrl: state.initializeSelectionFromUrl,
            navigateToArchitecture: state.navigateToArchitecture,
            openArchiveDetails: state.openArchiveDetails,
            openChangelogDetails: state.openChangelogDetails,
            openClusterDetails: state.openClusterDetails,
            openFamilyDetails: state.openFamilyDetails,
            openJobProfileDetails: state.openJobProfileDetails,
            openSidepanel: state.openSidepanel,
            openSkillsDetails: state.openSkillsDetails,
            setComparing: state.setComparing,
            setSelectedLevelId: state.setSelectedLevelId,
            setSelectedRoleId: state.setSelectedRoleId,
            updateJobProfileLanguage: state.updateJobProfileLanguage,
            updateSkillsPanelIfOpen: state.updateSkillsPanelIfOpen,
            updateSkillsProficiencyIfOpen: state.updateSkillsProficiencyIfOpen,
        })),
    );
};
